'use client';

import { useState, useMemo, useCallback, useRef } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
  CarouselDots,
  CarouselControls,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import { ComponentSection } from '@/app/_components/core/component-section';
import { Copytext } from '@/app/_components/core/copytext';
import { Pagination } from '@/app/_components/core/pagination';
import {
  getSectionProps,
  getIntroAlignmentClasses,
  getItemTrackId,
  getTrackingPrefix,
} from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import {
  EventBentoIntro,
  EventBentoCard,
  EventBentoFilters,
  ALL_VALUE,
} from './components';
import type { EventBentoComponentProps, EventItem } from './event-bento.types';

const BENTO_BOX_SIZE = 6;

const EVENT_CARD_SIZES_DEFAULT =
  '(min-width: 1920px) 342px, (min-width: 1280px) 278px, (min-width: 1024px) 424px, (min-width: 768px) 318px, (min-width: 640px) 482px, calc((100vw - 16px) * 0.8 - 24px * 0.2)';

const EVENT_CARD_SIZES_LARGE =
  '(min-width: 1920px) 362px, (min-width: 1280px) 298px';

const EVENT_CARD_SIZES_GRID =
  '(min-width: 1920px) 310px, (min-width: 1280px) 280px, (min-width: 1024px) 225px, (min-width: 768px) 340px, calc(100vw - 32px)';

function isLargeBentoCard(index: number, groupSize: number): boolean {
  return index === 0 || (groupSize === BENTO_BOX_SIZE && index === 5);
}

function chunkEvents(events: EventItem[]): EventItem[][] {
  return Array.from(
    { length: Math.ceil(events.length / BENTO_BOX_SIZE) },
    (_, i) => events.slice(i * BENTO_BOX_SIZE, (i + 1) * BENTO_BOX_SIZE)
  );
}

function EventBentoComponent({
  component,
  componentPosition,
  isNested,
}: EventBentoComponentProps) {
  const {
    intro,
    events: eventData,
    theme,
    displayType,
    useDateAsHeadline = false,
    displayFilters = false,
    itemsToDisplay,
    showNavigation = true,
    showDots = true,
    autoplay = false,
  } = component;

  const { items: allItems, filters } = eventData;

  const resolvedFilters = useMemo<typeof filters>(() => {
    if (!filters) return filters;

    // Use API filter arrays; fall back to extracting from items when empty
    const locations =
      filters.location?.length
        ? filters.location
        : [
            ...new Set(
              allItems
                .map((e) => e.filterLocation ?? e.location)
                .filter((loc): loc is string => !!loc)
            ),
          ];

    const categories =
      filters.categories?.length
        ? filters.categories
        : [
            ...new Set(
              allItems
                .map((e) => e.category)
                .filter((cat): cat is string => !!cat)
            ),
          ];

    return { location: locations, categories };
  }, [allItems, filters]);

  const [api, setApi] = useState<CarouselApi>();
  const [selectedLocation, setSelectedLocation] = useState(ALL_VALUE);
  const [selectedCategory, setSelectedCategory] = useState(ALL_VALUE);
  const [currentPage, setCurrentPage] = useState(1);
  const paginationRef = useRef<HTMLElement>(null);
  const isTheme1 = theme === 'theme1';
  const carouselLabel = useLabel('carousel.events', 'Events');
  const defaultButtonLabel = useLabel('toEvent', 'Zum Event');
  const noEventsLabel = useLabel('noEvents', 'Derzeit keine Events verfügbar');
  const showFilters = displayFilters && resolvedFilters;
  const filteredEvents = useMemo(() => {
    const locAll = selectedLocation === ALL_VALUE;
    const catAll = selectedCategory === ALL_VALUE;
    if (locAll && catAll) return allItems;
    return allItems.filter(
      (e) =>
        (locAll || (e.filterLocation ?? e.location) === selectedLocation) &&
        (catAll || e.category === selectedCategory)
    );
  }, [allItems, selectedLocation, selectedCategory]);

  const pageSize = itemsToDisplay ?? 0;
  // Pagination only applies to grid mode — bento/carousel modes scroll through
  // all events via the carousel, so paginating would hide items behind pages.
  const shouldPaginate = pageSize > 0 && displayType === 'grid';
  const totalPages = shouldPaginate
    ? Math.ceil(filteredEvents.length / pageSize)
    : 1;

  const events = useMemo(() => {
    if (!shouldPaginate) return filteredEvents;
    const start = (currentPage - 1) * pageSize;
    return filteredEvents.slice(start, start + pageSize);
  }, [filteredEvents, shouldPaginate, pageSize, currentPage]);

  const handleLocationChange = useCallback((value: string) => {
    setSelectedLocation(value);
    setCurrentPage(1);
  }, []);

  const handleCategoryChange = useCallback((value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  }, []);

  // On page change, keep the pagination bar pinned exactly where the user
  // clicked. The last page often holds fewer cards than a full page, so the
  // grid above the bar shrinks on switch and the bar would otherwise slide out
  // of the viewport ("jump away"). Rather than scrolling to a fixed anchor
  // (which moves the bar relative to where it was), we record the bar's
  // viewport position before the switch and, after React has re-rendered the
  // shorter grid, scroll by exactly the delta so the bar stays visually still.
  // Full pages produce a zero delta (no scroll); only a height change is
  // compensated. rAF runs after the re-render (final position) but before
  // paint (no visible jump).
  const handlePageChange = useCallback((page: number) => {
    const before = paginationRef.current?.getBoundingClientRect().top;
    setCurrentPage(page);
    requestAnimationFrame(() => {
      const after = paginationRef.current?.getBoundingClientRect().top;
      if (before == null || after == null) return;
      const delta = after - before;
      if (delta !== 0) window.scrollBy({ top: delta, behavior: 'auto' });
    });
  }, []);

  const sectionCn = cn(
    'mx-auto max-w-page py-section-padding-y-mobile xl:py-section-padding-y-desktop',
    isTheme1 ? 'dark bg-background' : 'bg-background'
  );
  const sectionProps = getSectionProps(component, componentPosition, isNested);
  const trackingPrefix = getTrackingPrefix(component);
  const btnLabel = (e: EventItem) => emptyToUndefined(e.buttonLabel) ?? defaultButtonLabel;
  // Stable per-card tracking key: prefers the event's own uid, falling back to
  // its ordinal in the rendered set. The item segment is always appended (also
  // for a single card) so the id stays format-stable as events are added.
  const cardTrackingPrefix = (event: EventItem, index: number) =>
    trackingPrefix
      ? `${trackingPrefix}-card-${getItemTrackId(event.id, index)}`
      : trackingPrefix;
  if (events.length === 0) {
    const alignmentClasses = getIntroAlignmentClasses(intro?.introAlignment);
    return (
      <ComponentSection
        isNested={isNested}
        {...sectionProps}
        className={sectionCn}
      >
        <div className="container">
          {intro && (
            <EventBentoIntro intro={intro} trackingPrefix={trackingPrefix} />
          )}
          {showFilters && (
            <EventBentoFilters
              filters={resolvedFilters}
              selectedLocation={selectedLocation}
              selectedCategory={selectedCategory}
              onLocationChange={handleLocationChange}
              onCategoryChange={handleCategoryChange}
            />
          )}
          <Copytext
            variant="large-muted"
            className={alignmentClasses.container}
          >
            {noEventsLabel}
          </Copytext>
        </div>
      </ComponentSection>
    );
  }

  const isBentoMode = displayType === 'bento' || displayType === 'carousel';
  const isGridMode = displayType === 'grid';
  const eventChunks = chunkEvents(events);
  const hasMultipleChunks = eventChunks.length > 1;
  const canScrollMobile = events.length > 1;
  const showPeek = isBentoMode && canScrollMobile;

  const showMobileCarousel = isBentoMode;
  const showDesktopCarousel = isBentoMode && hasMultipleChunks;
  const showBentoGrid = isBentoMode && !hasMultipleChunks;

  const carouselControls = (canScroll: boolean) =>
    (showNavigation || showDots) && canScroll ? (
      <CarouselControls className="mt-2 lg:mt-4">
        {showNavigation && <CarouselNavigation api={api} />}
        {showDots && <CarouselDots api={api} />}
      </CarouselControls>
    ) : null;

  return (
    <ComponentSection
      isNested={isNested}
      {...sectionProps}
      className={sectionCn}
    >
      <div className={cn('container', showPeek && 'pr-0 sm:pr-4 xl:pr-6')}>
        {intro && (
          <div className={showPeek ? 'pr-4 sm:pr-0' : undefined}>
            <EventBentoIntro intro={intro} trackingPrefix={trackingPrefix} />
          </div>
        )}

        {showFilters && (
          <div className={showPeek ? 'pr-4 sm:pr-0' : undefined}>
            <EventBentoFilters
              filters={resolvedFilters}
              selectedLocation={selectedLocation}
              selectedCategory={selectedCategory}
              onLocationChange={handleLocationChange}
              onCategoryChange={handleCategoryChange}
            />
          </div>
        )}

        {showMobileCarousel && (
          <Carousel
            opts={{
              align: 'start',
              loop: autoplay && canScrollMobile,
              watchDrag: canScrollMobile,
            }}
            autoplay={autoplay && canScrollMobile}
            setApi={setApi}
            ariaLabel={carouselLabel}
            className="w-full xl:hidden"
          >
            <CarouselContent className="ml-event-bento-ml-mobile pb-8 lg:ml-event-bento-ml-desktop">
              {events.map((event, i) => (
                <CarouselItem
                  key={event.id}
                  className="basis-(--carousel-slide-basis) pl-event-bento-pl-mobile md:basis-(--carousel-slide-basis-md) lg:pl-event-bento-pl-desktop"
                >
                  {/* Bento cards always render the title as headline (date stays inline);
                      the CMS-controlled `useDateAsHeadline` flag only takes effect in the grid mode below. */}
                  <EventBentoCard
                    event={event}
                    variant="default"
                    buttonLabel={btnLabel(event)}
                    sizes={EVENT_CARD_SIZES_DEFAULT}
                    useTitleAsHeadline
                    trackingPrefix={cardTrackingPrefix(event, i)}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {carouselControls(canScrollMobile)}
          </Carousel>
        )}

        {showDesktopCarousel && (
          <Carousel
            opts={{
              align: 'start',
              loop: autoplay && hasMultipleChunks,
              watchDrag: hasMultipleChunks,
            }}
            autoplay={autoplay && hasMultipleChunks}
            setApi={setApi}
            ariaLabel={carouselLabel}
            className="hidden w-full xl:block"
          >
            <CarouselContent className="pb-8">
              {eventChunks.map((chunk, ci) => (
                <CarouselItem key={ci} className="basis-full">
                  <div className="grid grid-cols-4 gap-event-bento-grid-gap-mobile xl:gap-event-bento-grid-gap-desktop">
                    {chunk.map((event, i) => {
                      const large = isLargeBentoCard(i, chunk.length);
                      return (
                        // Bento cards always render the title as headline;
                        // the CMS `useDateAsHeadline` flag is intentionally
                        // ignored here and only applied in the grid mode below.
                        <EventBentoCard
                          key={event.id}
                          event={event}
                          variant={large ? 'large' : 'default'}
                          buttonLabel={btnLabel(event)}
                          useTitleAsHeadline
                          sizes={
                            large
                              ? EVENT_CARD_SIZES_LARGE
                              : EVENT_CARD_SIZES_DEFAULT
                          }
                          className={large ? 'col-span-2' : ''}
                          trackingPrefix={cardTrackingPrefix(
                            event,
                            ci * BENTO_BOX_SIZE + i
                          )}
                        />
                      );
                    })}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {carouselControls(hasMultipleChunks)}
          </Carousel>
        )}

        {showBentoGrid && (
          <div className="hidden xl:grid xl:grid-cols-4 xl:gap-event-bento-grid-gap-desktop">
            {events.map((event, i) => {
              const large = isLargeBentoCard(i, events.length);
              return (
                // Bento cards always render the title as headline;
                // the CMS `useDateAsHeadline` flag is intentionally
                // ignored here and only applied in the grid mode below.
                <EventBentoCard
                  key={event.id}
                  event={event}
                  variant={large ? 'large' : 'default'}
                  buttonLabel={btnLabel(event)}
                  useTitleAsHeadline
                  sizes={
                    large ? EVENT_CARD_SIZES_LARGE : EVENT_CARD_SIZES_DEFAULT
                  }
                  className={large ? 'col-span-2' : ''}
                  trackingPrefix={cardTrackingPrefix(event, i)}
                />
              );
            })}
          </div>
        )}

        {isGridMode && (
          <div className="grid grid-cols-1 gap-event-bento-grid-gap-mobile md:grid-cols-2 xl:gap-event-bento-grid-gap-desktop">
            {events.map((event, i) => (
              // Grid mode honors the CMS `useDateAsHeadline` flag (title-first
              // when set, date-first when unset) — passed through 1:1 as the
              // EventCard `useTitleAsHeadline` prop, which has the same effect.
              <EventBentoCard
                key={event.id}
                event={event}
                variant="grid"
                buttonLabel={btnLabel(event)}
                useTitleAsHeadline={useDateAsHeadline}
                sizes={EVENT_CARD_SIZES_GRID}
                trackingPrefix={cardTrackingPrefix(event, i)}
              />
            ))}
          </div>
        )}

        {shouldPaginate && totalPages > 1 && (
          <Pagination
            ref={paginationRef}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            className="mt-8"
          />
        )}
      </div>
    </ComponentSection>
  );
}

export { EventBentoComponent };
export default EventBentoComponent;
